import { Plus, Trash2, ChevronUp, ChevronDown } from "lucide-react";
import type { JourneyStep, JourneyAction, FieldSpec } from "@ckgo2/shared";
import ConditionGroupEditor from "./ConditionGroupEditor";
import ActionEditor, { type BuilderOptions } from "./ActionEditor";

const sel =
  "rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-1 text-sm";
const lbl = "block text-xs font-medium text-gray-500 mb-1";

export default function StepCard({
  index,
  total,
  step,
  onChange,
  onRemove,
  onMove,
  fields,
  options,
}: {
  index: number;
  total: number;
  step: JourneyStep;
  onChange: (s: JourneyStep) => void;
  onRemove: () => void;
  onMove: (dir: -1 | 1) => void;
  fields: FieldSpec[];
  options: BuilderOptions;
}) {
  const delay = step.delay ?? {};
  const setDelay = (p: Partial<NonNullable<JourneyStep["delay"]>>) =>
    onChange({ ...step, delay: { ...delay, ...p } });

  const setAction = (i: number, a: JourneyAction) =>
    onChange({ ...step, actions: step.actions.map((x, idx) => (idx === i ? a : x)) });

  return (
    <div className="space-y-3 rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Step {index + 1}</h4>
        <div className="flex items-center gap-1">
          <button
            type="button"
            disabled={index === 0}
            onClick={() => onMove(-1)}
            className="rounded p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30"
          >
            <ChevronUp className="h-4 w-4" />
          </button>
          <button
            type="button"
            disabled={index === total - 1}
            onClick={() => onMove(1)}
            className="rounded p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30"
          >
            <ChevronDown className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={onRemove}
            className="rounded p-1 text-gray-400 hover:text-red-600"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Delay */}
      <div>
        <label className={lbl}>Wait before running</label>
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <input
            type="number"
            min={0}
            className={sel + " w-20"}
            value={delay.days ?? 0}
            onChange={(e) => setDelay({ days: Number(e.target.value) || 0 })}
          />
          <span className="text-gray-400">days</span>
          <input
            type="number"
            min={0}
            className={sel + " w-20"}
            value={delay.hours ?? 0}
            onChange={(e) => setDelay({ hours: Number(e.target.value) || 0 })}
          />
          <span className="text-gray-400">hours</span>
          <input
            className={sel + " w-24"}
            placeholder="HH:MM"
            value={delay.atTimeOfDay ?? ""}
            onChange={(e) => setDelay({ atTimeOfDay: e.target.value || undefined })}
          />
          <span className="text-gray-400">at time (optional)</span>
        </div>
      </div>

      {/* Conditions */}
      <div>
        <label className={lbl}>Run only if (optional)</label>
        <ConditionGroupEditor
          value={step.conditions ?? null}
          onChange={(conditions) => onChange({ ...step, conditions: conditions ?? undefined })}
          fields={fields}
        />
        {step.conditions && (
          <div className="mt-1 text-xs text-gray-500">
            If conditions fail:{" "}
            <select
              className={sel}
              value={step.ifConditionsFail ?? "skip_step"}
              onChange={(e) =>
                onChange({
                  ...step,
                  ifConditionsFail: e.target.value as "skip_step" | "exit_journey",
                })
              }
            >
              <option value="skip_step">skip this step</option>
              <option value="exit_journey">exit the journey</option>
            </select>
          </div>
        )}
      </div>

      {/* Actions */}
      <div>
        <label className={lbl}>Actions</label>
        <div className="space-y-2">
          {step.actions.map((a, i) => (
            <ActionEditor
              key={i}
              value={a}
              onChange={(na) => setAction(i, na)}
              onRemove={() =>
                onChange({ ...step, actions: step.actions.filter((_, idx) => idx !== i) })
              }
              options={options}
            />
          ))}
        </div>
        <button
          type="button"
          onClick={() =>
            onChange({
              ...step,
              actions: [
                ...step.actions,
                { type: "send_email", recipient: "customer" } as JourneyAction,
              ],
            })
          }
          className="text-primary-600 hover:text-primary-700 mt-2 inline-flex items-center gap-1 text-xs"
        >
          <Plus className="h-3.5 w-3.5" /> Add action
        </button>
      </div>
    </div>
  );
}
